<template>
    <div>
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4 f">
                <h1>{{ _mft('crm:leadPage.tabTitle.deals') }}</h1>

                <div class="flex items-center space-x-4 pr-4">
                    <a class="text-xs mf-link" :href="route('crm.settings')">{{ _mft('crm:dealTypes.manage') }}</a>
                </div>
            </div>
        </frosted-bar>


        <div class="space-y-6">
            <card-basic class="mt-10">
                <template v-slot:body>
                    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div class="p-4 rounded-xl bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200">
                            <p class="text-xs uppercase tracking-wide text-yellow-700">{{ _mft('crm:dealBoard.pendingDeals') }}</p>
                            <p class="text-2xl font-semibold text-yellow-900">{{ global.pending_count }}</p>
                            <p class="text-xs text-yellow-700 mt-1">{{ formatMoney(global.pending_value) }}</p>
                        </div>
                        <div class="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                            <p class="text-xs uppercase tracking-wide text-blue-700">{{ _mft('crm:dealBoard.weightedPipeline') }}</p>
                            <p class="text-2xl font-semibold text-blue-900">{{ formatMoney(global.weighted_value) }}</p>
                            <p class="text-xs text-blue-700 mt-1">{{ _mft('crm:dealBoard.basedOnStatusLikelihood') }}</p>
                        </div>
                        <div class="p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                            <p class="text-xs uppercase tracking-wide text-green-700">{{ _mft('crm:dealBoard.wonValue') }}</p>
                            <p class="text-2xl font-semibold text-green-900">{{ formatMoney(global.won_value) }}</p>
                            <p class="text-xs text-green-700 mt-1">{{ _mft('crm:dealBoard.closedWon') }}</p>
                        </div>
                        <div class="p-4 rounded-xl bg-gradient-to-br from-rose-50 to-rose-100 border border-rose-200">
                            <p class="text-xs uppercase tracking-wide text-rose-700">{{ _mft('crm:dealBoard.lostValue') }}</p>
                            <p class="text-2xl font-semibold text-rose-900">{{ formatMoney(global.lost_value) }}</p>
                            <p class="text-xs text-rose-700 mt-1">{{ _mft('crm:dealBoard.closedLost') }}</p>
                        </div>
                    </div>
                </template>
            </card-basic>

            <card-basic>
                <template v-slot:body>
                    <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div
                            v-for="bucket in global.likelihood_buckets"
                            :key="bucket.key"
                            class="p-3 rounded-lg border border-gray-200 bg-gray-50"
                        >
                            <div class="flex justify-between text-sm font-medium">
                                <span>{{ bucket.label }}</span>
                                <span>{{ bucket.count }}</span>
                            </div>
                            <p class="text-xs text-gray-500">{{ formatMoney(bucket.value) }}</p>
                            <div class="h-2 mt-2 rounded bg-gray-200 overflow-hidden">
                                <div
                                    class="h-full rounded bg-gray-700"
                                    :style="{ width: likelihoodBucketWidth(bucket) }"
                                ></div>
                            </div>
                        </div>
                    </div>
                </template>
            </card-basic>

            <card-basic>
                <template v-slot:body>
                    <div class="flex flex-wrap gap-2">
                        <button-base
                            :style-type="selectedDealTypeId === null ? 'tinted' : 'secondary'"
                            @click="selectDealType(null)"
                        >
                            <span class="font-semibold">{{ _mft('crm:dealBoard.allTypes') }}</span>
                        </button-base>
                        <button-base
                            v-for="type in dealTypes"
                            :key="type.id"
                            :style-type="selectedDealTypeId === type.id ? 'tinted' : 'secondary'"
                            @click="selectDealType(type.id)"
                        >
                            <span class="font-semibold">{{ type.type_name }}</span>
                            <span class="ml-2 text-xs opacity-80">{{ _mft('crm:dealBoard.pendingShort', { count: type.pending_count }) }}</span>
                            <span class="ml-2 text-xs opacity-80">{{ formatMoney(type.weighted_value) }}</span>
                        </button-base>
                    </div>

                    <card-sub
                        v-if="boardSummary"
                        class="mt-4 text-sm"
                    >
                        <span class="font-semibold">{{ boardSummary.pending_count }}</span>
                        {{ _mft('crm:dealBoard.openDealsInType') }},
                        {{ _mft('crm:dealBoard.valueLabel') }}
                        <span class="font-semibold">{{ formatMoney(boardSummary.pending_value) }}</span>,
                        {{ _mft('crm:dealBoard.weightedLabel') }}
                        <span class="font-semibold">{{ formatMoney(boardSummary.weighted_value) }}</span>
                    </card-sub>

                    <ui-tabbed class="mt-6">
                        <ui-tabbed-tab
                            :title="_mft('crm:dealBoard.tab.byStatus')"
                            value="by-status"
                            :active="true"
                        >
                            <div v-if="loading" class="py-8">
                                <mf-spinner-medium class="w-24 h-12" />
                            </div>

                            <card-basic v-else-if="dealTypes.length === 0">
                                <template v-slot:body>
                                    <div class="flex flex-col items-center">
                                        <p class="text-sm text-gray-500 text-center">{{ _mft('crm:dealBoard.noDealTypesYet') }}</p>
                                        <a class="text-xs mf-link" :href="route('crm.settings')">
                                            <button-primary>
                                                {{ _mft('crm:dealTypes.manage') }}
                                            </button-primary>
                                        </a>
                                    </div>
                                </template>
                            </card-basic>

                            <div v-else class="overflow-x-auto pb-2 max-w-full">
                                <div class="grid gap-4 grid-cols-1 lg:grid-cols-3">
                                    <div
                                        v-for="column in displayColumns"
                                        :key="column.id"
                                        class="rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col"
                                        :class="column.synthetic ? 'opacity-90' : ''"
                                    >
                                        <div class="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
                                            <div class="flex items-start justify-between gap-2">
                                                <div>
                                                    <h3 class="text-sm font-semibold text-gray-900">
                                                        {{ column.name }}
                                                        <a
                                                            :href="route('crm.settings')+'#deal-statuses-by-type'"
                                                            class="-translate-y-1 inline-block"
                                                        >
                                                            <i class="fa-regular fa-pencil text-xxs ml-1 text-gray-400 opacity-50 hover:opacity-100"></i>
                                                        </a>
                                                    </h3>
                                                    <p v-if="selectedDealTypeId === null && column.deal_type_names.length > 0" class="text-xs text-gray-500">
                                                        {{ column.deal_type_names.join(', ') }}
                                                    </p>
                                                    <p class="text-xs text-gray-500">{{ _mft('crm:dealBoard.likelihoodLabel', { value: column.likelihood }) }}</p>
                                                </div>
                                                <span
                                                    class="text-xs px-2 py-1 rounded"
                                                    :class="column.is_closed ? 'bg-gray-200 text-gray-700' : 'bg-emerald-100 text-emerald-700'"
                                                >
                                                    {{ column.is_closed ? _mft('crm:dealBoard.closedState') : _mft('crm:dealBoard.openState') }}
                                                </span>
                                            </div>
                                            <div class="mt-2 text-xs text-gray-600">
                                                {{ _mft('crm:dealBoard.dealsCount', { count: column.totals.count }) }}
                                                <span class="mx-1">•</span>
                                                {{ formatMoney(column.totals.value) }}
                                            </div>
                                        </div>

                                        <draggable
                                            v-model="column.deals"
                                            item-key="id"
                                            class="p-3 space-y-3 min-h-[180px]"
                                            ghost-class="deal-card-ghost"
                                            :disabled="selectedDealTypeId === null"
                                            :group="columnDragGroup(column)"
                                            @change="onBoardChange($event, column)"
                                        >
                                            <template #item="{ element }">
                                                <article class="p-3 rounded-lg border border-gray-200 bg-white shadow-sm">
                                                    <a
                                                        v-if="element.lead_url"
                                                        :href="element.lead_url+'#deals'"
                                                        target="_blank"
                                                        class="text-sm font-semibold mf-link"
                                                    >
                                                        {{ element.lead_name || _mft('crm:dealBoard.unnamedLead') }} <i class="fa-regular fa-arrow-up-right-from-square text-xxs ml-1 -translate-y-1"></i>
                                                    </a>
                                                    <p v-else class="text-sm font-semibold text-gray-800">{{ element.lead_name || _mft('crm:dealBoard.unnamedLead') }}</p>
                                                    <p class="text-xs text-gray-500 truncate mt-1">{{ element.lead_company }}</p>
                                                    <div class="mt-3 flex items-center justify-between text-xs">
                                                        <span class="font-semibold text-gray-900">{{ formatMoney(element.value) }}</span>
                                                        <span class="text-gray-700">{{ _mft('crm:dealBoard.weightedWithValue', { value: formatMoney(element.weighted_value) }) }}</span>
                                                    </div>
                                                    <div class="mt-2 flex items-center justify-between gap-2">
                                                        <p class="text-xs text-gray-500 truncate" v-if="element.owner_name">
                                                            {{ _mft('crm:dealBoard.ownerLabel', { name: element.owner_name }) }}
                                                        </p>
                                                        <span v-else></span>
                                                        <button
                                                            type="button"
                                                            class="text-xs font-semibold text-myflowMagenta hover:underline"
                                                            @mousedown.stop
                                                            @touchstart.stop
                                                            @click.stop="openInSlideout(element)"
                                                        >
                                                            {{ _mft('crm:leadDealCard.edit') }}
                                                        </button>
                                                    </div>
                                                </article>
                                            </template>
                                        </draggable>
                                    </div>
                                </div>
                            </div>
                        </ui-tabbed-tab>

                        <ui-tabbed-tab
                            :title="_mft('crm:dealBoard.tab.weightedChart')"
                            value="chart"
                            :active="false"
                        >
                            <div class="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h3 class="text-sm font-semibold text-gray-900">{{ _mft('crm:dealBoard.weightedPipelineTraceability') }}</h3>
                                    <p class="text-xs text-gray-500">
                                        {{ _mft('crm:dealBoard.startLabel') }}: {{ history.start_date || '-' }}
                                        <span class="mx-1">•</span>
                                        {{ _mft('crm:dealBoard.endLabel') }}: {{ history.end_date || '-' }}
                                    </p>
                                </div>

                                <div>
                                    <label for="excludeDeal" class="text-xs font-semibold  text-gray-900">
                                        {{ _mft('crm:dealBoard.excludeOneDeal') }}
                                    </label>
                                    <select
                                        id="excludeDeal"
                                        v-model="excludedDealId"
                                        class="block w-full py-1 text-sm border rounded-wl-inner px-2"
                                        @change="onExcludedDealChanged"
                                    >
                                        <option value="">{{ _mft('crm:dealBoard.includeAllDeals') }}</option>
                                        <option
                                            v-for="option in history.exclude_options"
                                            :key="option.id"
                                            :value="String(option.id)"
                                        >
                                            {{ option.label }} ({{ formatMoney(option.value) }})
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div class="mt-4">
                                <chart-line
                                    :labels="history.labels"
                                    :series="historySeries"
                                    :fill-colors="['#2563eb', '#16a34a', '#dc2626']"
                                    :show-tooltip="true"
                                    :show-legend="true"
                                ></chart-line>
                            </div>

                            <p class="mt-3 text-xs text-gray-500">
                                {{ _mft('crm:dealBoard.addedRemovedHelp') }}
                            </p>
                        </ui-tabbed-tab>
                    </ui-tabbed>
                </template>
            </card-basic>
        </div>

        <deal-edit-slideout
            v-model:open="editSlideoutOpen"
            :deal="currentlyEditingDeal"
            @update:deal="onEditingDealChanged"
            :type-options="editingTypeOptions"
            :status-options="editingStatusOptions"
            :busy="editingDealWorking"
            :disabled="!currentlyEditingDealIsValid"
            :delete-url="currentlyEditingDealDeleteUrl"
            @submit="updateDeal"
        />
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import DealEditSlideout from './forms/dealEditSlideout.vue';

export default {
    components: {
        draggable,
        DealEditSlideout,
    },
    props: {
        currency: {
            type: String,
            default: 'SEK',
        },
    },
    data() {
        return {
            loading: true,
            moving: false,
            selectedDealTypeId: null,
            dealTypes: [],
            global: {
                pending_count: 0,
                pending_value: 0,
                weighted_value: 0,
                won_value: 0,
                lost_value: 0,
                likelihood_buckets: [],
            },
            boardSummary: null,
            columns: [],
            editSlideoutOpen: false,
            editingDealWorking: false,
            currentlyEditingDeal: {
                id: '',
                type: null,
                status: null,
                value: null,
            },
            editingTypeOptions: [],
            statusOptionsByType: {},
            excludedDealId: '',
            history: {
                start_date: null,
                end_date: null,
                labels: [],
                weighted_pipeline: [],
                added_weighted: [],
                removed_weighted: [],
                net_change: [],
                exclude_options: [],
            },
        };
    },
    methods: {
        async loadBoardData() {
            this.loading = true;

            try {
                const response = await axios.get(this.route('crm.deals.boardData'), {
                    params: {
                        deal_type_id: this.selectedDealTypeId || undefined,
                        exclude_deal_id: this.excludedDealId !== '' ? Number(this.excludedDealId) : undefined,
                    },
                });

                this.dealTypes = response.data.deal_types ?? [];
                this.selectedDealTypeId = response.data.selected_deal_type_id ?? null;
                this.global = response.data.global ?? this.global;
                this.boardSummary = response.data.board?.summary ?? null;
                this.columns = response.data.board?.columns ?? [];
                this.history = response.data.history ?? this.history;

                if (
                    this.excludedDealId !== '' &&
                    !this.history.exclude_options.some((option) => String(option.id) === String(this.excludedDealId))
                ) {
                    this.excludedDealId = '';
                }
            } finally {
                this.loading = false;
            }
        },
        async selectDealType(typeId) {
            this.selectedDealTypeId = typeId;
            this.excludedDealId = '';
            await this.loadBoardData();
        },
        async onExcludedDealChanged() {
            await this.loadBoardData();
        },
        async onBoardChange(event, column) {
            if (this.selectedDealTypeId === null) {
                await this.loadBoardData();
                return;
            }

            if (this.moving) {
                return;
            }

            if (!event.added) {
                return;
            }

            const deal = event.added.element;
            const statusId = this.statusIdForDealInColumn(deal, column);
            if (!deal || !deal.id || !statusId) {
                await this.loadBoardData();
                return;
            }

            this.moving = true;

            try {
                await axios.post(this.route('crm.deals.moveStatus', deal.id), {
                    status_id: statusId,
                });

                await this.loadBoardData();
            } catch (error) {
                await this.loadBoardData();
            } finally {
                this.moving = false;
            }
        },
        async loadEditingDealTypes() {
            const response = await axios.get(this.route('crm.deal.types'));
            this.editingTypeOptions = response.data ?? [];

            const statusOptionsByType = {};
            this.editingTypeOptions.forEach((type) => {
                statusOptionsByType[type.id] = type.statuses || [];
            });

            this.statusOptionsByType = statusOptionsByType;
            this.ensureStatusValueForEditingDeal();
        },
        openInSlideout(deal) {
            this.currentlyEditingDeal = {
                id: deal.id,
                type: deal.type_id ? Number(deal.type_id) : (deal.type ? Number(deal.type) : null),
                status: deal.status ?? null,
                value: deal.value,
            };

            this.ensureStatusValueForEditingDeal();
            this.editSlideoutOpen = true;
        },
        onEditingDealChanged(updatedDeal) {
            this.currentlyEditingDeal = updatedDeal;
        },
        async updateDeal() {
            if (!this.currentlyEditingDealIsValid) {
                return;
            }

            this.editingDealWorking = true;
            const payload = {
                ...this.currentlyEditingDeal,
                type: this.currentlyEditingDeal.type ? Number(this.currentlyEditingDeal.type) : null,
            };

            try {
                await axios.post(this.route('crm.deal.update', this.currentlyEditingDeal.id), payload);
                this.editSlideoutOpen = false;
                await this.loadBoardData();
            } finally {
                this.editingDealWorking = false;
            }
        },
        buildStatusOptions(typeId, currentStatus = null) {
            const statuses = this.statusOptionsByType[typeId] || [];
            const options = statuses.map((status) => ({
                value: status.name,
                label: `${status.name} (${status.likelihood}%)`,
            }));

            if (
                currentStatus &&
                !options.some((option) => option.value === currentStatus)
            ) {
                options.unshift({
                    value: currentStatus,
                    label: `${currentStatus} (${_mft('crm:leadDealCard.legacyStatus')})`,
                });
            }

            return options;
        },
        ensureStatusValueForEditingDeal() {
            const currentDeal = this.currentlyEditingDeal;
            if (!currentDeal || !currentDeal.type) {
                return;
            }

            const options = this.buildStatusOptions(Number(currentDeal.type), currentDeal.status);
            if (options.length === 0) {
                return;
            }

            if (!currentDeal.status || !options.some((option) => option.value === currentDeal.status)) {
                currentDeal.status = options[0].value;
            }
        },
        likelihoodBucketWidth(bucket) {
            const max = this.global.pending_count || 1;
            return `${Math.min(100, (bucket.count / max) * 100)}%`;
        },
        formatMoney(value) {
            const numericValue = Number(value || 0);

            try {
                return new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: this.currency,
                    maximumFractionDigits: 0,
                }).format(numericValue);
            } catch (error) {
                return `${new Intl.NumberFormat().format(numericValue)} ${this.currency}`;
            }
        },
        statusIdForDealInColumn(deal, column) {
            if (!deal || !column) {
                return null;
            }

            if (column.status_ids_by_type && deal.type_id) {
                return column.status_ids_by_type[deal.type_id] ?? null;
            }

            return column.status_id ?? null;
        },
        columnDragGroup(column) {
            if (this.selectedDealTypeId === null) {
                return {
                    name: 'deal-board-all',
                    pull: false,
                    put: false,
                };
            }

            return {
                name: 'deal-board-' + column.deal_type_id,
                pull: true,
                put: column.status_id !== null || Object.keys(column.status_ids_by_type || {}).length > 0,
            };
        },
    },
    computed: {
        displayColumns() {
            if (this.selectedDealTypeId !== null) {
                return this.columns.map((column) => ({
                    ...column,
                    deal_type_names: column.deal_type_name ? [column.deal_type_name] : [],
                    status_ids_by_type: column.deal_type_id && column.status_id ? { [column.deal_type_id]: column.status_id } : {},
                }));
            }

            const groupedColumns = {};

            this.columns.forEach((column) => {
                const key = (column.name || '').trim().toLowerCase();
                if (!key) {
                    return;
                }

                if (!groupedColumns[key]) {
                    groupedColumns[key] = {
                        ...column,
                        id: 'status-name:' + key,
                        deal_type_id: null,
                        deal_type_name: null,
                        deal_type_names: [],
                        status_id: null,
                        status_ids_by_type: {},
                        likelihoods: [],
                        deals: [],
                        totals: {
                            count: 0,
                            value: 0,
                            weighted_value: 0,
                        },
                    };
                }

                const groupedColumn = groupedColumns[key];

                if (column.deal_type_name && !groupedColumn.deal_type_names.includes(column.deal_type_name)) {
                    groupedColumn.deal_type_names.push(column.deal_type_name);
                }

                if (column.deal_type_id && column.status_id) {
                    groupedColumn.status_ids_by_type[column.deal_type_id] = column.status_id;
                }

                if (!groupedColumn.likelihoods.includes(column.likelihood)) {
                    groupedColumn.likelihoods.push(column.likelihood);
                }

                groupedColumn.synthetic = groupedColumn.synthetic && column.synthetic;
                groupedColumn.is_closed = groupedColumn.is_closed && column.is_closed;
                groupedColumn.is_won = groupedColumn.is_won || column.is_won;
                groupedColumn.deals.push(...(column.deals || []));
                groupedColumn.totals.count += column.totals?.count ?? 0;
                groupedColumn.totals.value += column.totals?.value ?? 0;
                groupedColumn.totals.weighted_value += column.totals?.weighted_value ?? 0;
            });

            return Object.values(groupedColumns)
                .map((column) => {
                    const likelihoods = column.likelihoods.sort((a, b) => a - b);
                    return {
                        ...column,
                        likelihood: likelihoods.length === 1 ? likelihoods[0] : `${likelihoods[0]}-${likelihoods[likelihoods.length - 1]}`,
                        deals: column.deals.sort((a, b) => b.value - a.value),
                    };
                })
                .sort((a, b) => {
                    if (a.position === b.position) {
                        return a.name.localeCompare(b.name);
                    }

                    return a.position - b.position;
                });
        },
        editingStatusOptions() {
            if (!this.currentlyEditingDeal.type) {
                return [];
            }

            return this.buildStatusOptions(Number(this.currentlyEditingDeal.type), this.currentlyEditingDeal.status);
        },
        currentlyEditingDealIsValid() {
            if (isNaN(this.currentlyEditingDeal.value)) {
                return false;
            }

            if (
                !this.currentlyEditingDeal.type ||
                !this.currentlyEditingDeal.status ||
                this.currentlyEditingDeal.value === null
            ) {
                return false;
            }

            return true;
        },
        currentlyEditingDealDeleteUrl() {
            if (!this.currentlyEditingDeal.id) {
                return '';
            }

            return this.route('crm.deal.delete', this.currentlyEditingDeal.id);
        },
        historySeries() {
            let cumulativeAdded = 0;
            const addedData = (this.history.added_weighted || []).map((v) => {
                cumulativeAdded += v || 0;
                return cumulativeAdded;
            });

            let cumulativeRemoved = 0;
            const removedData = (this.history.removed_weighted || []).map((v) => {
                cumulativeRemoved += v || 0;
                return cumulativeRemoved;
            });

            return [
                {
                    name: _mft('crm:dealBoard.series.weightedPipeline'),
                    data: this.history.weighted_pipeline || [],
                },
                {
                    name: _mft('crm:dealBoard.series.addedWeighted'),
                    data: addedData,
                },
                {
                    name: _mft('crm:dealBoard.series.removedWeighted'),
                    data: removedData,
                },
            ];
        },
    },
    watch: {
        'currentlyEditingDeal.type': function () {
            this.ensureStatusValueForEditingDeal();
        },
    },
    mounted() {
        this.loadBoardData();
        this.loadEditingDealTypes();
    },
};
</script>

<style scoped>
.deal-card-ghost {
    opacity: 0.45;
}
</style>
